/**
 * SOLID Review
 * S — Good. One concern: the table loop's shells — every use case React triggers and every server
 *     event Infrastructure delivers, each as load → decide → save around at most one server call.
 *     No rule lives here; the Domain's decisions hold them all.
 * O — Good. A new move is one method composed of `step` and one gateway call.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the ops of `ClientUseCases` (table part) and `ClientServerEvents`.
 * D — Strong. Five port interfaces, all injected; no concrete adapter, no store, no React.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import { DONE, outcomeOf, type ClientSteps, type TDecision, type TOutcome, type TResult } from '../shared/ClientSteps';
import type {
  ICommandNothing, ICommandAnnounce, ICommandLeave, ICommandGameStarted, ICommandRoundMoveAnswered, ICommandPeeked,
  ICommandClaimAccepted, ICommandBelieveAccepted, ICommandPlayAgainAnswered, ICommandActiveRoundRestored,
  ICommandSuspendedReconciled, ICommandLobbyActiveRound, ICommandLobbyRoomLoaded, IPayloadRoomRead,
} from '../../domain/ports/schemas/TableMoves';
import type {
  IPortClientState, IPortClientRoundServer, IPortClientRoomServer, IPortClientServerWatch, IPayloadWatchTarget,
  IPayloadServerStartRound, IPayloadServerClaim, IPayloadServerRoomCode,
} from '../ports/contracts';
import type { TTableEvent } from '../ports/ClientServerEvents.contract';

export interface IConfigDepTableContext {
  readonly decisions: IPortClientDecisions;
  readonly state: IPortClientState;
  readonly rounds: IPortClientRoundServer;
  readonly rooms: IPortClientRoomServer;
  readonly watch: IPortClientServerWatch;
  /** The one serial section, shared with the session (FE2). */
  readonly steps: ClientSteps;
}

const NOTHING: ICommandNothing = {};

/**
 * THE TABLE LOOP'S SHELLS (frontend strict-ports plan, FE1, 2026-10-03). What used to be a dozen click
 * handlers and subscription callbacks, each reading the store, calling an adapter and writing the store
 * in its own way, is one pattern:
 *
 *     step:   snapshot → decide → apply        (inside the SERIAL section, no I/O)
 *     move:   step(plan) → server call → step(answer)
 *     event:  step(the event's decision)
 *
 * THE SERIAL SECTION is `ClientSteps` (FE2: one queue for the table and the session). A server call
 * sits BETWEEN two steps, never inside one, so a slow server cannot hold the table. The second step
 * re-reads the table, as the handler's `useGameStore.getState()` after its await did.
 *
 * An event the guards drop and a move the plan refuses are ordinary outcomes, not errors: a refused
 * tap answers its refusal, and React ignores it, as the handler's early `return` did.
 */
export class TableContext {
  constructor(private readonly deps: IConfigDepTableContext) {}

  /** Load the table, decide, save what changed — one indivisible step, on the shared queue. */
  private step<D>(ctx: IContextRequest, decide: (input: IPropsClientTableInput) => Promise<TDecision<D>>): Promise<TResult<D>> {
    return this.deps.steps.table(ctx, decide);
  }

  // ── the local player's moves ──────────────────────────────────────────────────────────────────

  async startGame(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planStartGame(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const opening: IPayloadServerStartRound = { roomId: plan.value.roomId, rollerId: plan.value.playerId };
    const answer = await rounds.startRound(ctx, opening);
    if (!answer.success) return answer;
    const started: ICommandGameStarted = { request: plan.value, answer: answer.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.gameStarted(ctx, input, started)));
  }

  async rollDice(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planRoll(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const sent = await rounds.rollDice(ctx, plan.value);
    if (!sent.success) return sent;
    const rolled: ICommandRoundMoveAnswered = { request: plan.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.rolled(ctx, input, rolled)));
  }

  async peekDice(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planPeek(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const dice = await rounds.peekDice(ctx, plan.value);
    if (!dice.success) return dice;
    const peeked: ICommandPeeked = { request: plan.value, answer: dice.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.peeked(ctx, input, peeked)));
  }

  /** An open claim after a peek, a blind pass before one — the plan says which. */
  async announce(ctx: IContextRequest, command: ICommandAnnounce): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planAnnounce(ctx, input, command));
    if (!plan.success) return plan;
    const claim: IPayloadServerClaim = { roundId: plan.value.roundId, playerId: plan.value.playerId, score: plan.value.score };
    const sent = plan.value.blind ? await rounds.announceBlind(ctx, claim) : await rounds.announceClaim(ctx, claim);
    if (!sent.success) return sent; // a refused blind pass must not advance the phase
    const accepted: ICommandClaimAccepted = { request: plan.value, answer: sent.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.claimAccepted(ctx, input, accepted)));
  }

  async believe(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planBelieve(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const round = await rounds.believeClaim(ctx, plan.value);
    if (!round.success) return round;
    const accepted: ICommandBelieveAccepted = { request: plan.value, answer: round.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.believeAccepted(ctx, input, accepted)));
  }

  async rollFirst(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planRollFirst(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const round = await rounds.believeClaim(ctx, plan.value);
    if (!round.success) return round;
    const accepted: ICommandBelieveAccepted = { request: plan.value, answer: round.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.rollFirstAccepted(ctx, input, accepted)));
  }

  /** The plan clears the server's decider before the call; the table moves whatever the server says. */
  async liftCup(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planLift(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const sent = await rounds.liftCup(ctx, plan.value);
    const lifted: ICommandRoundMoveAnswered = { request: plan.value };
    const after = await this.step(ctx, (input) => decisions.liftAnswered(ctx, input, lifted));
    return sent.success ? outcomeOf(after) : sent;
  }

  /** The plan restores the lives before the call; the answer — or its absence — follows. */
  async playAgain(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planPlayAgain(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const opening: IPayloadServerStartRound = { roomId: plan.value.roomId, rollerId: plan.value.rollerId };
    const round = await rounds.startRound(ctx, opening);
    const answered: ICommandPlayAgainAnswered = { request: plan.value, answer: round.success ? round.value : null };
    const after = await this.step(ctx, (input) => decisions.playAgainAnswered(ctx, input, answered));
    return round.success ? outcomeOf(after) : round;
  }

  /**
   * Leave the table: reset it, then tell the server — best effort and never awaited, as the exit button
   * did. Its arguments were read before the reset, so the order is the handler's in effect.
   */
  async leaveTable(ctx: IContextRequest, command: ICommandLeave): Promise<TOutcome> {
    const left = await this.step(ctx, (input) => this.deps.decisions.leaveTable(ctx, input, command));
    if (!left.success) return left;
    if (left.value.leave !== null) void this.deps.rooms.leaveRoom(ctx, left.value.leave);
    return DONE;
  }

  // ── keeping the table level with the server ───────────────────────────────────────────────────

  /**
   * Catch up after a gap: the active round, then the room's suspended list — two independent reads,
   * each applied only if it answered, as the hook's two try blocks were.
   */
  async resync(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds, rooms } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planResync(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const read: IPayloadRoomRead = { roomId: plan.value.roomId };
    const round = await rounds.getActiveRoundForRestore(ctx, read);
    if (round.success) {
      const restored: ICommandActiveRoundRestored = { request: plan.value, activeRound: round.value };
      await this.step(ctx, (input) => decisions.activeRoundRestored(ctx, input, restored));
    }
    const code: IPayloadServerRoomCode = { roomCode: plan.value.roomCode };
    const lookup = await rooms.getRoomByCode(ctx, code);
    if (lookup.success) {
      const reconciled: ICommandSuspendedReconciled = { request: plan.value, suspendedPlayers: lookup.value?.suspendedPlayers ?? null };
      await this.step(ctx, (input) => decisions.suspendedReconciled(ctx, input, reconciled));
    }
    return DONE;
  }

  async loadLobbyRoom(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rooms } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planLobbyRead(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const roster = await rooms.getRoom(ctx, plan.value);
    if (!roster.success) return roster;
    const loaded: ICommandLobbyRoomLoaded = { request: plan.value, roster: roster.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.lobbyRoomLoaded(ctx, input, loaded)));
  }

  async lobbyPollTick(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, rounds } = this.deps;
    const plan = await this.step(ctx, (input) => decisions.planLobbyRead(ctx, input, NOTHING));
    if (!plan.success) return plan;
    const round = await rounds.getActiveRound(ctx, plan.value);
    if (!round.success) return round;
    const polled: ICommandLobbyActiveRound = { request: plan.value, activeRound: round.value };
    return outcomeOf(await this.step(ctx, (input) => decisions.lobbyActiveRound(ctx, input, polled)));
  }

  /** Watch the streams of the table as it stands: its room, and its round's. Idempotent downstream. */
  async watchTable(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const loaded = await this.deps.state.snapshot(ctx);
    if (!loaded.success) return loaded;
    const target: IPayloadWatchTarget = { roomId: loaded.value.table.room?.roomId ?? null, roundId: loaded.value.table.activeRoundId };
    return this.deps.watch.watch(ctx, target);
  }

  async unwatchTable(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    return this.deps.watch.unwatch(ctx);
  }

  // ── the server's events ───────────────────────────────────────────────────────────────────────

  /**
   * One server event, decided and saved. A connection that comes back after a gap owes a resync,
   * started here and not awaited: the delivery is answered at once, and live events keep arriving
   * while the catch-up reads run.
   */
  async deliver(ctx: IContextRequest, event: TTableEvent): Promise<TOutcome> {
    const d = this.deps.decisions;
    switch (event.type) {
      case 'roundStarted': return outcomeOf(await this.step(ctx, (input) => d.roundStarted(ctx, input, event.payload)));
      case 'claimAnnounced': return outcomeOf(await this.step(ctx, (input) => d.claimAnnounced(ctx, input, event.payload)));
      case 'cupLifted': return outcomeOf(await this.step(ctx, (input) => d.cupLifted(ctx, input, event.payload)));
      case 'diceRolled': return outcomeOf(await this.step(ctx, (input) => d.diceRolled(ctx, input, event.payload)));
      case 'roomUpdated': return outcomeOf(await this.step(ctx, (input) => d.roomUpdated(ctx, input, event.payload)));
      case 'playerSuspended': return outcomeOf(await this.step(ctx, (input) => d.playerSuspended(ctx, input, event.payload)));
      case 'playerResumed': return outcomeOf(await this.step(ctx, (input) => d.playerResumed(ctx, input, event.payload)));
      case 'roomAbandoned': return outcomeOf(await this.step(ctx, (input) => d.roomAbandoned(ctx, input, event.payload)));
      case 'decisionCountdown': return outcomeOf(await this.step(ctx, (input) => d.decisionCountdown(ctx, input, event.payload)));
      case 'tableView': return outcomeOf(await this.step(ctx, (input) => d.tableView(ctx, input, event.payload)));
      case 'connectivity': {
        const decided = await this.step(ctx, (input) => d.connectivity(ctx, input, event.payload));
        if (decided.success && decided.value.resync) void this.resync(ctx, NOTHING);
        return outcomeOf(decided);
      }
    }
  }
}
